import { TMarker } from "maps/MapLeaflet";
import { getEarliestAvailablePhase } from "_common/_utils/inventoryUtils";
import { getShortLabelOfPhase, getSizeFromAvailablePhase } from "_common/_utils/getLabelOfPhase";
import { ADMIN_RESOURCE } from "admin/_common/photos/AdminPhotosStore";
import { TAddressMdl, TInventory } from "properties/_models/PropertyMdl";
import {
    calculateAndFormatPropertyPrice,
    getCompleteAddresse,
    getPriceOfProperty,
    isInPesosProperty,
} from "_common/_utils/propertyUtils";
import { FavoriteCardProps } from "properties/propertyCard/types/favorite-card.type";
import { propertiesStore } from "properties/_stores/propertiesStore";
import { convert, UNIT_CONVERSION } from "_common/_utils/unityUtils";
import i18next from "i18next";
import {
    features,
    getFees,
    getPropertyAmenities,
    getSurfacePriceSquareAndFt,
    getUnitViews,
    surface,
} from "_common/_utils/unitUtils";
import { CURRENCY_OPTIONS } from "_common/currency/CurrencyTypes";
import { COMMON_RESOURCE } from "_common/resources/BaseResourceStore";
import { TCompare } from "properties/compare/_models/properties-favorite.model";

export class ItemMapper {
    static toMarker(item: any, markerType: "property" | "unit" | "activity" | "community"): TMarker | undefined {
        if (markerType === "activity" && item?.location?.lat && item?.location?.lng) {
            return {
                id: item?._id ?? item?.localized.title,
                markerType: "activity",
                urlAlias: item?.urlAlias,
                photos: item?.photos,
                title: item?.localized.title,
                description: item?.localized.description,
                coordinates: {
                    lat: item?.location?.lat,
                    lng: item?.location?.lng,
                },
                icon: item.icon,
            } as TMarker;
        }

        if (
            markerType === "unit" &&
            item?.property?.location?.coordinates[1] &&
            item?.property?.location?.coordinates[0]
        ) {
            return {
                id: item._id,
                markerType: "unit",
                title: item?.property?.localized?.title,
                description: item?.property?.localized?.description,
                urlAlias: item?.property?.localized?.urlAlias + "/units/",
                type: item?.property.type,
                photos: item?.photos,
                priceInPesos: item?.priceInPesos,
                price: item.price?.min,
                beds: {
                    min: item?.bedrooms,
                    max: 0,
                },
                squareSurface: item?.property.squareSurface,
                surface: item?.property.min,
                coordinates: {
                    lat: item.property.location.coordinates[1],
                    lng: item.property.location.coordinates[0],
                },
            } as TMarker;
        }

        if (markerType === "property" && item?.location?.coordinates[1] && item?.location?.coordinates[0]) {
            return {
                id: item._id,
                markerType: "property",
                title: item.localized.title,
                description: item.localized.description,
                urlAlias: item.localized.urlAlias,
                nbOfUnitsAvailable: item.nbOfUnitsAvailable,
                type: item.type,
                photos: item.photos,
                priceInPesos: item.priceInPesos?.min,
                price: item.price?.min,
                priceIsNotDisplayed: item.priceIsNotDisplayed,
                beds: { min: item.bedrooms.min, max: item.bedrooms.max },
                squareSurface: item.squareSurface,
                surface: item.squareSurface.min,
                coordinates: { lat: item.location.coordinates[1], lng: item.location.coordinates[0] },
                features: item.features,
                unitsDetails: item.unitsDetails,
                labelMarker: getShortLabelOfPhase(getEarliestAvailablePhase(item.inventory?.phases ?? [])),
                sizeMarker: getSizeFromAvailablePhase(getEarliestAvailablePhase(item.inventory?.phases ?? [])),
                showLabel: false,
            };
        }

        if (
            markerType === "community" &&
            item?.geoZone?.location?.coordinates[1] &&
            item?.geoZone?.location?.coordinates[0]
        ) {
            return {
                id: item?._id ?? item?.localized.title,
                markerType: "community",
                urlAlias: item?.localized.urlAlias,
                photos: item?.photos,
                title: item?.localized.title,
                description: item?.localized.description,
                unitsCount: Number(item?.unitsCount),
                propertiesCount: Number(item?.propertiesCount),
                totalSurface: item?.totalSurface,
                geoZoneId: item?.geoZoneId,
                geoZone: {
                    city: item?.geoZone?.address?.city,
                    provinceLong: item?.geoZone?.address?.provinceLong,
                    neighbourhood: item?.geoZone?.address?.neighbourhood,
                },
                coordinates: {
                    lat: item?.geoZone?.location?.coordinates[1],
                    lng: item?.geoZone?.location?.coordinates[0],
                },
            } as TMarker;
        }
        return undefined;
    }

    static toFavorite(item: any): FavoriteCardProps {
        return {
            _id: item._id,
            resource: ADMIN_RESOURCE.PROPERTIES,
            photos: item?.photos,
            type: item?.type,
            title: item?.localized.title,
            price: {
                priceMin: item?.price.min,
                priceInPesosMin: item?.priceInPesos.min,
                isInPesosProperty: isInPesosProperty(item),
                isPriceNotDisplayed: item?.priceIsNotDisplayed,
                isNotFrom: false,
            },
            bedrooms: {
                min: item?.bedrooms.min,
                max: item?.bedrooms.max,
            },
            bathrooms: item?.bathrooms,
            address: {
                city: item?.address.city,
                country: item?.address.country,
                extra: item?.address.extra,
                postalCode: item?.address.postalCode,
                province: item?.address.province,
                provinceShort: item?.address.provinceShort,
                street: item?.address.street,
            } as TAddressMdl,
            inventory: {
                phases: item?.inventory.phases,
            } as TInventory,
            discount: item?.discount,
            discountText: item?.localized.discountText,
            urlAlias: item?.localized.urlAlias,
            squareSurfaceMin: item?.squareSurface.min,
        };
    }

    static toCompare(item: any, type: COMMON_RESOURCE.PROPERTIES | COMMON_RESOURCE.UNIT): TCompare {
        if (type === COMMON_RESOURCE.PROPERTIES) {
            return {
                resource: COMMON_RESOURCE.PROPERTIES,
                id: item._id,
                photo: item.photos?.[0]?.url ?? "https://placehold.co/300x200?text=Zisla.com",
                title: item.localized.title,
                address: getCompleteAddresse(item),
                urlAlias: item.localized.urlAlias,
                nbOfUnitsAvailable: item.nbOfUnitsAvailable,
                type: item.type,
                bedrooms: { min: item.bedrooms.min, max: item.bedrooms.max },
                bathrooms: { min: item.bathrooms.min, max: item.bathrooms.max },
                price: getPriceOfProperty(item),
                priceCurrency: "",
                priceSurface: "/",
                surface: [
                    item.squareSurface.min
                        ? i18next.t("propertiesPage.card.surfaces", {
                              ft2: convert(item.squareSurface.min, UNIT_CONVERSION.M2_TO_FT2_BACK),
                              m2: Math.round(item.squareSurface.min * 100) / 100,
                          })
                        : "",
                    ...[
                        item.interiorConstruction
                            ? [
                                  i18next.t("unitPage.header.interiorConstruction"),
                                  surface(item.interiorConstruction),
                              ].join(": ")
                            : "",
                        item.exteriorConstruction
                            ? [
                                  i18next.t("unitPage.header.exteriorConstruction"),
                                  surface(item.exteriorConstruction),
                              ].join(": ")
                            : "",
                        item.terraceSurface
                            ? [i18next.t("unitPage.header.terraceSurface"), surface(item.terraceSurface)].join(": ")
                            : "",
                        item.rooftopSurface
                            ? [i18next.t("unitPage.header.rooftopSurface"), surface(item.rooftopSurface)].join(": ")
                            : "",
                        item.yardSize ? [i18next.t("unitPage.header.yardSize"), surface(item.yardSize)].join(": ") : "",
                        item.poolSurface
                            ? [i18next.t("unitPage.header.poolSurface"), surface(item.poolSurface)].join(": ")
                            : "",
                        item.storageSurface
                            ? [i18next.t("unitPage.header.storageSurface"), surface(item.storageSurface)].join(": ")
                            : "",
                        item.parkingSurface
                            ? [i18next.t("unitPage.header.parkingSurface"), surface(item.parkingSurface)].join(": ")
                            : "",
                        item.totalConstructArea
                            ? [i18next.t("unitPage.header.totalConstructArea"), surface(item.totalConstructArea)].join(
                                  ": ",
                              )
                            : "",
                        item.lotSurface
                            ? [i18next.t("unitPage.header.lotSurface"), surface(item.lotSurface)].join(": ")
                            : "",
                        item.cus ? [i18next.t("unitPage.header.cus"), surface(item.cus)].join(": ") : "",
                        item.cos ? [i18next.t("unitPage.header.cos"), surface(item.cos)].join(": ") : "",
                    ].filter(Boolean),
                ],
                fees: i18next.t(`propertyPage.feesTypes.${item.fees.type}`, {
                    count: item.fees.value,
                }),
                closingInPesos: isInPesosProperty(item),
                features: features(item.features),
                amenities: getPropertyAmenities(item) ?? [],
                parkings: item?.parkings ?? "",
                views: [],
            };
        }

        const property = propertiesStore.getSync(item.propertyId);
        const fees = property?.fees?.applyOnUnits ? property?.fees : item?.fees;
        return {
            resource: COMMON_RESOURCE.UNIT,
            id: item._id,
            photo: item.photos?.[0]?.url ?? property?.photos?.[0]?.url ?? "https://placehold.co/300x200?text=Zisla.com",
            title: item.variant ? item.name + " - " + item.variant : item.name,
            address: getCompleteAddresse(property!),
            urlAlias: item.name,
            parentUrlAlias: property!.localized.urlAlias,
            nbOfUnitsAvailable: 0,
            type: property!.type,
            parentType: property!.type,
            bedrooms: { min: item.bedrooms, max: 0 },
            bathrooms: { min: item.bathrooms, max: 0 },
            parkings: item?.parkings ?? "",
            price: calculateAndFormatPropertyPrice(
                {
                    priceMin: item.price,
                    priceInPesosMin: item.priceInPesos,
                    priceIsInPesos: isInPesosProperty(property!),
                },
                CURRENCY_OPTIONS.MXN,
            ),
            priceCurrency: "",
            priceSurface: getSurfacePriceSquareAndFt(item.bySquareSurface),
            features: features(item.features, "unit.features."),
            parentFeatures: features(property!.features),
            amenities: getPropertyAmenities(property!) ?? [],
            views: getUnitViews(item) ?? [],
            fees: i18next.t(`propertyPage.feesTypes.${fees.type}`, {
                count: Number(getFees(fees, item)),
            }),
            closingInPesos: isInPesosProperty(property!),
            surface: [
                item.interiorConstruction
                    ? [i18next.t("unitPage.header.interiorConstruction"), surface(item.interiorConstruction)].join(": ")
                    : "",
                item.exteriorConstruction
                    ? [i18next.t("unitPage.header.exteriorConstruction"), surface(item.exteriorConstruction)].join(": ")
                    : "",
                item.terraceSurface
                    ? [i18next.t("unitPage.header.terraceSurface"), surface(item.terraceSurface)].join(": ")
                    : "",
                item.rooftopSurface
                    ? [i18next.t("unitPage.header.rooftopSurface"), surface(item.rooftopSurface)].join(": ")
                    : "",
                item.yardSize ? [i18next.t("unitPage.header.yardSize"), surface(item.yardSize)].join(": ") : "",
                item.poolSurface
                    ? [i18next.t("unitPage.header.poolSurface"), surface(item.poolSurface)].join(": ")
                    : "",
                item.storageSurface
                    ? [i18next.t("unitPage.header.storageSurface"), surface(item.storageSurface)].join(": ")
                    : "",
                item.parkingSurface
                    ? [i18next.t("unitPage.header.parkingSurface"), surface(item.parkingSurface)].join(": ")
                    : "",
                item.totalConstructArea
                    ? [i18next.t("unitPage.header.totalConstructArea"), surface(item.totalConstructArea)].join(": ")
                    : "",
                item.lotSurface ? [i18next.t("unitPage.header.lotSurface"), surface(item.lotSurface)].join(": ") : "",
                item.cus ? [i18next.t("unitPage.header.cus"), surface(item.cus)].join(": ") : "",
                item.cos ? [i18next.t("unitPage.header.cos"), surface(item.cos)].join(": ") : "",
            ].filter(Boolean),
        };
    }
}
